/**
 * ForecastReadinessPanel - Shows forecast availability status per building/asset.
 *
 * Props:
 *   - community: Community object with buildings and assets
 *   - forecasts: Array of forecast result objects (with asset_id and date)
 *   - targetDate: The date for which we're checking forecasts (tomorrow for day-ahead)
 *
 * Checks real results data via resultsAPI when available, and falls back to
 * dataset-based heuristics (checking if datasets exist for each building/asset).
 */

import React, { useMemo, useState, useEffect } from 'react'
import { Badge, Alert } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { resultsAPI } from '../api/results'

function ForecastReadinessPanel({ community, forecasts = [], targetDate }) {
  const { t } = useTranslation()
  const buildings = community?.buildings || []
  const communityAssets = community?.community_assets || []
  const datasets = community?.community_datasets || community?.datasets || []
  const communityId = community?.id

  // Try to load real saved results to determine readiness
  const [savedResults, setSavedResults] = useState([])

  useEffect(() => {
    if (!communityId) return
    resultsAPI.list(communityId)
      .then(results => {
        setSavedResults(Array.isArray(results) ? results : [])
      })
      .catch(() => {
        // Backend unavailable — fall back to dataset-based heuristics
        setSavedResults([])
      })
  }, [communityId])

  const readinessItems = useMemo(() => {
    // Check for saved forecast results
    const forecastResults = savedResults.filter(r => r.result_type === 'forecast')

    // Load forecasts: check if each building has a load dataset or saved forecast
    const loadItems = buildings.map((building) => {
      // Check if there's a saved forecast result for this building
      // The save stores: parameters.asset (asset name), parameters.forecast_type, and name includes building_id
      const hasForecastResult = forecastResults.some(r =>
        r.parameters?.asset_id === building.id ||
        r.parameters?.building_id === building.id ||
        r.parameters?.asset === building.building_id ||
        r.name?.toLowerCase().includes(building.building_id?.toLowerCase())
      )

      // Check if there's a load dataset for this building
      const hasDataset = (building.datasets || []).some(d =>
        d.kind === 'load' || d.dataset_type?.includes('load')
      )

      let status = 'missing'
      let lastUpdated = null

      if (hasForecastResult) {
        const match = forecastResults.find(r =>
          r.parameters?.building_id === building.id ||
          r.parameters?.asset === building.building_id ||
          r.name?.toLowerCase().includes(building.building_id?.toLowerCase())
        )
        status = 'ready'
        lastUpdated = match?.created_at
          ? new Date(match.created_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
          : t('forecast_readiness.recently')
      } else if (hasDataset) {
        status = 'ready'
        lastUpdated = t('forecast_readiness.dataset_available')
      }

      return {
        id: `load_${building.id}`,
        type: 'load',
        label: t('forecast_readiness.load_forecast_label', { building: building.building_id }),
        status,
        lastUpdated,
        link: `/communities/${community.id}/forecasting`,
      }
    })

    // PV generation forecasts: per-asset readiness (mirrors per-building load pattern)
    const pvAssets = communityAssets.filter(a => ['solar_pv_farm', 'ground_mounted_pv', 'pv'].includes(a.asset_type))
    // Also include rooftop PV from buildings
    const buildingPVAssets = buildings.flatMap(b =>
      (b.building_assets || [])
        .filter(a => a.asset_type === 'rooftop_pv')
        .map(a => ({ ...a, buildingId: b.building_id, buildingUUID: b.id }))
    )
    const allPVAssets = [
      ...pvAssets.map(a => ({ id: a.id, name: a.name || t('forecast_readiness.solar_pv_farm'), category: 'pv', capacity: a.specs?.capacity_kwp })),
      ...buildingPVAssets.map(a => ({ id: `${a.buildingUUID}_rooftop_pv`, name: `${a.buildingId} ${t('forecast_readiness.rooftop_pv_suffix')}`, category: 'pv', capacity: a.specs?.capacity_kwp })),
    ]

    const pvItems = allPVAssets.length > 0
      ? allPVAssets.map(asset => {
          // Check for a saved forecast result matching this specific asset
          const hasForecastResult = forecastResults.some(r =>
            r.parameters?.forecast_type === 'generation' &&
            (r.parameters?.asset_category === 'pv' || !r.parameters?.asset_category) &&
            (r.parameters?.asset_id === asset.id || r.parameters?.asset === asset.name ||
             r.name?.toLowerCase().includes(asset.name.toLowerCase()))
          )

          let status = 'missing'
          let lastUpdated = null
          if (hasForecastResult) {
            const match = forecastResults.find(r =>
              r.parameters?.forecast_type === 'generation' &&
              (r.parameters?.asset_id === asset.id || r.parameters?.asset === asset.name ||
               r.name?.toLowerCase().includes(asset.name.toLowerCase()))
            )
            status = 'ready'
            lastUpdated = match?.created_at
              ? new Date(match.created_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
              : t('forecast_readiness.forecast_saved')
          }

          return {
            id: `gen_pv_${asset.id}`,
            type: 'generation',
            label: t('forecast_readiness.generation_forecast_label', { name: `${asset.name}${asset.capacity ? ` (${asset.capacity} kWp)` : ''}` }),
            status,
            lastUpdated,
            link: `/communities/${community.id}/forecasting`,
          }
        })
      : [{
          id: 'gen_pv',
          type: 'generation',
          label: t('forecast_readiness.pv_generation_forecast'),
          status: 'not_applicable',
          lastUpdated: null,
          link: null,
        }]

    // Wind generation forecasts: per-asset readiness
    const windAssets = communityAssets.filter(a => a.asset_type === 'wind_turbine')
    const windItems = windAssets.length > 0
      ? windAssets.map(asset => {
          const hasWindForecastResult = forecastResults.some(r =>
            r.parameters?.forecast_type === 'generation' &&
            r.parameters?.asset_category === 'wind' &&
            (r.parameters?.asset_id === asset.id || r.parameters?.asset === (asset.name || t('forecast_readiness.wind_turbine')) ||
             r.name?.toLowerCase().includes((asset.name || 'wind').toLowerCase()))
          )

          let status = 'missing'
          let lastUpdated = null
          if (hasWindForecastResult) {
            const match = forecastResults.find(r =>
              r.parameters?.forecast_type === 'generation' &&
              r.parameters?.asset_category === 'wind' &&
              (r.parameters?.asset_id === asset.id || r.parameters?.asset === (asset.name || t('forecast_readiness.wind_turbine')))
            )
            status = 'ready'
            lastUpdated = match?.created_at
              ? new Date(match.created_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
              : t('forecast_readiness.forecast_saved')
          }

          return {
            id: `gen_wind_${asset.id}`,
            type: 'generation',
            label: t('forecast_readiness.generation_forecast_label', { name: `${asset.name || t('forecast_readiness.wind_turbine')}${asset.specs?.capacity_kw ? ` (${asset.specs.capacity_kw} kW)` : ''}` }),
            status,
            lastUpdated,
            link: `/communities/${community.id}/forecasting`,
          }
        })
      : [{
          id: 'gen_wind',
          type: 'generation',
          label: t('forecast_readiness.wind_generation_forecast'),
          status: 'not_applicable',
          lastUpdated: null,
          link: null,
        }]

    // Tariff data: check historical and day-ahead separately
    const historicalTariffDs = datasets.filter(d =>
      d.dataset_type === 'energy_tariff_data' || d.dataset_type === 'feed_in_tariff_data'
    )
    const dayAheadTariffDs = datasets.filter(d =>
      d.dataset_type === 'day_ahead_import_tariff' || d.dataset_type === 'day_ahead_export_tariff'
    )
    const hasCommunityMeta = community?.metadata?.import_tariff > 0

    const tariffItems = [
      {
        id: 'tariff-historical',
        type: 'tariff',
        label: t('forecast_readiness.historical_tariff'),
        status: historicalTariffDs.length > 0 ? 'ready' : (hasCommunityMeta ? 'warning' : 'missing'),
        lastUpdated: historicalTariffDs.length > 0
          ? t('forecast_readiness.dataset_count', { count: historicalTariffDs.length })
          : (hasCommunityMeta ? t('forecast_readiness.using_community_settings') : null),
        link: `/communities/${community.id}/data`,
      },
      {
        id: 'tariff-day-ahead',
        type: 'tariff',
        label: t('forecast_readiness.day_ahead_tariff'),
        status: dayAheadTariffDs.length > 0 ? 'ready' : 'warning',
        lastUpdated: dayAheadTariffDs.length > 0
          ? t('forecast_readiness.dataset_count', { count: dayAheadTariffDs.length })
          : t('forecast_readiness.optional_fallback'),
        link: `/communities/${community.id}/data`,
      },
    ]

    return [...loadItems, ...pvItems, ...windItems, ...tariffItems]
  }, [buildings, communityAssets, datasets, community, savedResults, t])

  const readyCount = readinessItems.filter(item => item.status === 'ready').length
  const missingCount = readinessItems.filter(item => item.status === 'missing').length
  const totalRequired = readinessItems.filter(item => item.status !== 'not_applicable').length

  const overallReady = missingCount === 0

  return (
    <div className="forecast-readiness-panel">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h6 style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, margin: 0 }}>
            {t('forecast_readiness.title')}
          </h6>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)', margin: 0 }}>
            {t('forecast_readiness.status_for', { date: targetDate || t('forecast_readiness.optimization_date') })}
          </p>
        </div>
        <Badge
          bg="none"
          style={{
            backgroundColor: overallReady ? '#D1F2EB' : '#FFF3CD',
            color: overallReady ? 'var(--color-success)' : 'var(--color-warning)',
            fontSize: 'var(--font-size-xs)',
            padding: 'var(--space-2) var(--space-3)',
          }}
        >
          {t('forecast_readiness.ready_count', { ready: readyCount, total: totalRequired })}
        </Badge>
      </div>

      {/* Readiness Checklist */}
      <div className="forecast-readiness-list">
        {readinessItems.map((item) => (
          <div key={item.id} className="forecast-readiness-item">
            <div className="forecast-readiness-icon">
              {item.status === 'ready' && (
                <span style={{ color: 'var(--color-success)', fontSize: '1.1rem' }}>✓</span>
              )}
              {item.status === 'missing' && (
                <span style={{ color: 'var(--color-danger)', fontSize: '1.1rem' }}>✗</span>
              )}
              {item.status === 'warning' && (
                <span style={{ color: 'var(--color-warning)', fontSize: '1.1rem' }}>⚠</span>
              )}
              {item.status === 'not_applicable' && (
                <span style={{ color: 'var(--color-neutral-400)', fontSize: '1.1rem' }}>—</span>
              )}
            </div>
            <div className="forecast-readiness-content">
              <div className="forecast-readiness-label">{item.label}</div>
              {item.lastUpdated && (
                <div className="forecast-readiness-meta">{item.lastUpdated}</div>
              )}
              {item.status === 'not_applicable' && (
                <div className="forecast-readiness-meta">{t('forecast_readiness.no_assets')}</div>
              )}
              {(item.status === 'missing' || item.status === 'warning') && item.link && (
                <Link
                  to={item.link}
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-primary)',
                    textDecoration: 'none',
                  }}
                >
                  {item.status === 'missing' ? t('forecast_readiness.run_forecast_link') : t('forecast_readiness.upload_data_link')}
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Overall Status Alert */}
      {!overallReady && (
        <Alert variant="warning" className="mt-3 mb-0" style={{ fontSize: 'var(--font-size-xs)' }}>
          <strong>{t('forecast_readiness.missing_alert_title')}</strong> {t('forecast_readiness.missing_alert_text')}{' '}
          <Link to={`/communities/${community.id}/forecasting`}>{t('forecast_readiness.forecasting_page')}</Link> {t('forecast_readiness.for_better_results')}
        </Alert>
      )}

      {overallReady && (
        <Alert variant="success" className="mt-3 mb-0" style={{ fontSize: 'var(--font-size-xs)' }}>
          <strong>{t('forecast_readiness.all_ready_title')}</strong> {t('forecast_readiness.all_ready_text')}
        </Alert>
      )}
    </div>
  )
}

export default ForecastReadinessPanel
